import React, { useState } from 'react';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  NativeSelect,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  CircularProgress
} from '@mui/material';
import { useCreate, useGo, useModal, useNavigation, useNotification, useOne, useResourceParams } from '@refinedev/core';
import { Edit } from '@refinedev/mui';
import { useForm } from '@refinedev/react-hook-form';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Controller } from 'react-hook-form';

import { AiReactJsonSchemaForm } from '@attackiq/components';
import { ReactAsf } from '@attackiq/react-asf';

import ContentModules from '../../components/ContentModules';
import { JSONEditor, MarkdownEditor } from '../../components/inputs';
import TabPanel from '../../components/mui/TabPanel/TabPanel';
import { SetContentLastUpdatedDate } from '../../components/SetContentLastUpdatedDate';
import { Scenario, ScenarioType } from '../../types';
import httpClient from '../../utils/axios';
import { getIsReactJsonSchemaScenarios } from '../../utils/getIsReactJsonSchema';

import HistoryDrawer from './components/HistoryDrawer';
import ScenarioMitigationsTab from './components/ScenarioMitigationsTab';
import ScenarioMitreMitigations from './components/ScenarioMitreMitigations/ScenarioMitreMitigations';
import ScenarioTags from './components/ScenarioTags';
import ScenarioUnifiedMitigationsTab from './components/ScenarioUnifiedMitigationsTab/ScenarioUnifiedMitigationsTab';

const ScenarioTabs = {
  General: 0,
  Model: 1,
  Platforms: 2,
  UnifiedMitigations: 3,
  DetectionRules: 4,
  MitreMitigations: 5,
  OldMitigations: 6,
  Modules: 7
} as const;

type ScenarioTabs = (typeof ScenarioTabs)[keyof typeof ScenarioTabs];

const SCENARIO_TABS_THAT_SAVE_ON_THE_FLY = [
  ScenarioTabs.UnifiedMitigations,
  ScenarioTabs.DetectionRules,
  ScenarioTabs.OldMitigations
];
type SaveOnTheFlyTabs = (typeof SCENARIO_TABS_THAT_SAVE_ON_THE_FLY)[number];

const ScenarioEdit = () => {
  const [tab, setTab] = useState<ScenarioTabs>(ScenarioTabs.General);
  const [cloningInProgress, setCloningInProgress] = useState<boolean>(false);
  const { id } = useResourceParams();
  const {
    refineCore: { query: queryResult, formLoading },
    register,
    control,
    handleSubmit,
    formState: { errors }
  } = useForm({
    refineCoreProps: {
      queryOptions: {
        queryKey: ['scenarios', id],
        staleTime: 0,
        structuralSharing: false
      }
    }
  });
  const scenarioTemplateId = queryResult?.data?.data.scenario_template;
  const {
    result: scenarioTemplateResponse,
    query: { isLoading }
  } = useOne({
    resource: 'scenario_templates',
    id: queryResult?.data?.data.scenario_template,
    queryOptions: {
      queryKey: ['scenario_templates', scenarioTemplateId],
      enabled: !!scenarioTemplateId
    }
  });

  const go = useGo();
  const { editUrl } = useNavigation();

  const { open } = useNotification();

  const { mutateAsync: createMutateAsync } = useCreate();

  const currentTabSavesOnTheFly = SCENARIO_TABS_THAT_SAVE_ON_THE_FLY.includes(tab as SaveOnTheFlyTabs);

  const isInstanceOfScenarioTemplate = queryResult?.data?.data.id === scenarioTemplateResponse?.scenario_instance;

  const drawer = useModal();

  const isReactJsonSchema = getIsReactJsonSchemaScenarios(scenarioTemplateResponse?.descriptor_json?.resources?.[0]);

  const handleTabChange = (_event: React.SyntheticEvent, value: number) => {
    setTab(value as ScenarioTabs);
  };

  const onSubmit = handleSubmit(async validData => {
    if (validData.indicator_pattern === '') {
      delete validData.indicator_pattern;
    }
    if (isInstanceOfScenarioTemplate) {
      delete validData.scenario_template;
      delete validData.model_json;
    }
    if (validData.descriptor_json) {
      if (Object.keys(validData.descriptor_json.schema_output).length === 0) {
        delete validData.descriptor_json;
      }
    }

    // TODO: Once the v2/scenarios/:id is removed and the v1 endpoint works as the v2 endpoint, we can remove this
    // onFinish would hit PATCH /v1/scenarios/:id. The controller for v1 is not persisting changes made to the model_json or indicattor_patterns
    // onFinish(validData);

    // We manually hit PATCH /v2/scenarios/id instead
    try {
      await httpClient.patch(`/v2/scenarios/${id}`, validData);
      go({
        to: {
          resource: 'scenarios',
          action: 'list'
        }
      });
    } catch (e) {
      open?.({
        type: 'error',
        message: 'Error saving scenario'
      });
      console.error(e);
    }
  });

  const handleCloneScenario = async () => {
    const scenario = queryResult?.data?.data as Scenario;
    scenario.name = `${scenario.name} ${new Date().toISOString()}`;

    setCloningInProgress(true);
    try {
      const response = await createMutateAsync({
        resource: `scenarios/${id}/save_copy`,
        values: scenario,
        successNotification: {
          type: 'success',
          message: 'Scenario cloned successfully'
        }
      });

      const url = editUrl('scenarios', response.data.id as string);

      window.open(url, '_blank');
    } catch (e) {
      console.error(e);
    } finally {
      setCloningInProgress(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}
    >
      <Paper variant="outlined">
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="subtitle2">Scenario ID: {queryResult?.data?.data.id}</Typography>
            <Typography variant="subtitle2">
              Scenario Template:{' '}
              <Link href={editUrl('scenario_templates', queryResult?.data?.data.scenario_template)}>
                {queryResult?.data?.data.scenario_template}
              </Link>
              <br />
            </Typography>
            <Link href={scenarioTemplateResponse?.zip_file} target="_blank">
              Download Scenario
            </Link>
            <Box sx={{ marginTop: 1 }}>
              <Button
                type="button"
                variant="contained"
                onClick={() => {
                  drawer.show();
                }}
              >
                HISTORY
              </Button>
            </Box>
            <br />
          </Box>
          <Box>
            <Button variant="contained" disabled={cloningInProgress} onClick={() => handleCloneScenario()}>
              Clone this scenario
            </Button>
          </Box>
        </Box>
      </Paper>
      <Edit
        canDelete
        deleteButtonProps={{
          confirmTitle: 'Do you want to delete Scenario (SCENARIO!!!)?'
        }}
        isLoading={formLoading || isLoading}
        footerButtons={({ defaultButtons }) => (
          <>
            <SetContentLastUpdatedDate<Scenario> resourceNameKey="name" />
            {defaultButtons}
          </>
        )}
        saveButtonProps={{
          onClick: onSubmit,
          disabled: currentTabSavesOnTheFly
        }}
      >
        <Box
          component="form"
          onSubmit={onSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          <Controller
            rules={{ required: 'Scenario name is required' }}
            name="name"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextField
                value={value || ''}
                onChange={onChange}
                error={!!errors.name}
                helperText={typeof errors.name?.message === 'string' ? errors.name?.message : undefined}
                placeholder="Scenario name"
                label="Scenario name"
                autoFocus
                fullWidth
              />
            )}
          />
        </Box>
        <Box
          sx={{
            paddingY: 2
          }}
        >
          <Paper square variant="outlined">
            <Tabs value={tab} onChange={handleTabChange} aria-label="Sections of the scenario">
              <Tab label="General" disabled={isLoading} />
              <Tab label="Model" disabled={isLoading} />
              <Tab label="Platforms" disabled={isLoading} />
              <Tab label="Unified Mitigations" disabled={isLoading} />
              <Tab label="Detection Rules" disabled={isLoading} />
              <Tab label="Mitre Mitigations" disabled={isLoading} />
              <Tab label="Old Mitigations" disabled={isLoading} />
              <Tab label="Modules" disabled={isLoading} />
            </Tabs>

            <TabPanel value={tab} index={ScenarioTabs.General}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>Scenario description*</AccordionSummary>
                <AccordionDetails>
                  <Controller
                    control={control}
                    name="description"
                    rules={{ required: 'Scenario description is required' }}
                    render={({ field: { onChange, value } }) => {
                      if (formLoading || isLoading) {
                        return <>Loading...</>;
                      }
                      return <MarkdownEditor value={value || ''} onChange={onChange} readonly={false} />;
                    }}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>Description JSON - Summary</AccordionSummary>
                <AccordionDetails>
                  <Controller
                    control={control}
                    name="description_json.summary"
                    render={({ field: { onChange, value } }) => {
                      if (formLoading || isLoading) {
                        return <>Loading...</>;
                      }
                      return <MarkdownEditor value={value || ''} onChange={onChange} readonly={false} />;
                    }}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>Description JSON - Prerequisites</AccordionSummary>
                <AccordionDetails>
                  <Controller
                    control={control}
                    name="description_json.prerequisites"
                    render={({ field: { onChange, value } }) => {
                      if (formLoading || isLoading) {
                        return <>Loading...</>;
                      }
                      return <MarkdownEditor value={value || ''} onChange={onChange} readonly={false} />;
                    }}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>Description JSON - Failure Criteria</AccordionSummary>
                <AccordionDetails>
                  <Controller
                    control={control}
                    name="description_json.failure_criteria"
                    render={({ field: { onChange, value } }) => {
                      if (formLoading || isLoading) {
                        return <>Loading...</>;
                      }
                      return <MarkdownEditor value={value || ''} onChange={onChange} readonly={false} />;
                    }}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  Description JSON - Prevention Criteria
                </AccordionSummary>
                <AccordionDetails>
                  <Controller
                    control={control}
                    name="description_json.prevention_criteria"
                    render={({ field: { onChange, value } }) => {
                      if (formLoading || isLoading) {
                        return <>Loading...</>;
                      }
                      return <MarkdownEditor value={value || ''} onChange={onChange} readonly={false} />;
                    }}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  Description JSON - Additional Information
                </AccordionSummary>
                <AccordionDetails>
                  <Controller
                    control={control}
                    name="description_json.additional_information"
                    defaultValue={queryResult?.data?.data.description_json?.additional_information}
                    render={({ field: { onChange, value } }) => {
                      if (formLoading || isLoading) {
                        return <>Loading...</>;
                      }
                      return <MarkdownEditor value={value || ''} onChange={onChange} readonly={false} />;
                    }}
                  />
                </AccordionDetails>
              </Accordion>

              <Controller
                control={control}
                name="time_to_live"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value || ''}
                    onChange={onChange}
                    error={!!errors.time_to_live}
                    helperText={
                      typeof errors.time_to_live?.message === 'string' ? errors.time_to_live?.message : undefined
                    }
                    label="Time to live (ms)"
                    fullWidth
                  />
                )}
              />

              <Controller
                control={control}
                name="indicator_pattern"
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value || ''}
                    onChange={onChange}
                    error={!!errors.indicator_pattern}
                    helperText={
                      typeof errors.indicator_pattern?.message === 'string'
                        ? errors.indicator_pattern?.message
                        : undefined
                    }
                    label="Indicator pattern"
                    fullWidth
                  />
                )}
              />

              <FormControlLabel
                label="Cancellable"
                control={
                  <Controller
                    name="cancellable"
                    control={control}
                    render={({ field }) => {
                      return <Checkbox onChange={e => field.onChange(e.target.checked)} checked={!!field.value} />;
                    }}
                  />
                }
              />

              <FormControl fullWidth>
                <InputLabel variant="standard" htmlFor="scenario-type">
                  Scenario type
                </InputLabel>
                <NativeSelect
                  defaultValue={queryResult?.data?.data?.scenario_type}
                  inputProps={{
                    name: 'scenario_type',
                    id: 'scenario-type'
                  }}
                  {...register('scenario_type', { required: true })}
                >
                  <option value={ScenarioType.Attack}>Attack</option>
                  <option value={ScenarioType.Validation}>Validation</option>
                </NativeSelect>
              </FormControl>
            </TabPanel>

            <TabPanel value={tab} index={ScenarioTabs.Model}>
              <Controller
                control={control}
                name="parameters_description"
                defaultValue={queryResult?.data?.data.parameters_description}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value || ''}
                    onChange={onChange}
                    fullWidth
                    multiline
                    label="Parameters description"
                    minRows={4}
                    maxRows={5}
                  />
                )}
              />

              <Typography variant="h6">Parameters:</Typography>
              <Controller
                name="model_json"
                control={control}
                defaultValue={queryResult?.data?.data.model_json}
                render={({ field }) => {
                  const resource = scenarioTemplateResponse?.descriptor_json?.resources?.[0];
                  const { form, schema } = resource;
                  const uiSchema = cloneDeep(get(resource, 'uiSchema', {}));

                  if (isInstanceOfScenarioTemplate) {
                    schema.readonly = true;
                  }
                  if (isLoading) return <CircularProgress />;

                  return isReactJsonSchema ? (
                    <AiReactJsonSchemaForm
                      defaultFormData={field.value}
                      onChange={field.onChange}
                      schema={schema}
                      uiSchema={uiSchema}
                      id="data-scenario-detail-form"
                    />
                  ) : (
                    <ReactAsf onModelChange={field.onChange} schema={schema} form={form} model={field.value} />
                  );
                }}
              />
            </TabPanel>

            <TabPanel value={tab} index={ScenarioTabs.Platforms}>
              <Typography variant="h6">Supported platforms:</Typography>
              <Controller
                name="supported_platforms"
                control={control}
                defaultValue={queryResult?.data?.data.supported_platforms}
                render={({ field: { value, onChange } }) => {
                  return <JSONEditor value={value} onChange={onChange} />;
                }}
              />
            </TabPanel>
            <TabPanel value={tab} index={ScenarioTabs.UnifiedMitigations}>
              <DndProvider backend={HTML5Backend}>
                <ScenarioUnifiedMitigationsTab show_mitigations={true} show_detection_rules={false} />
              </DndProvider>
            </TabPanel>
            <TabPanel value={tab} index={ScenarioTabs.DetectionRules}>
              <DndProvider backend={HTML5Backend}>
                <ScenarioUnifiedMitigationsTab show_mitigations={false} show_detection_rules={true} />
              </DndProvider>
            </TabPanel>
            <TabPanel value={tab} index={ScenarioTabs.MitreMitigations}>
              <DndProvider backend={HTML5Backend}>
                <ScenarioMitreMitigations />
              </DndProvider>
            </TabPanel>
            <TabPanel value={tab} index={ScenarioTabs.OldMitigations}>
              <DndProvider backend={HTML5Backend}>
                <ScenarioMitigationsTab />
              </DndProvider>
            </TabPanel>

            <TabPanel value={tab} index={ScenarioTabs.Modules}>
              <ContentModules contentType="scenario" />
            </TabPanel>
          </Paper>
        </Box>
      </Edit>
      <Card elevation={1}>
        <CardHeader title={<Typography variant="h5">Edit Scenario Tags</Typography>} />
        <CardContent>
          <ScenarioTags />
        </CardContent>
      </Card>
      <HistoryDrawer drawer={drawer} selectedId={id as string} model="Scenario" />
    </Box>
  );
};

export default ScenarioEdit;
